import { Button, message, Select, Space, Spin } from "antd";
import { DownloadOutlined, EditOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { serverFetch } from "../../server";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import Build from "./build";
import dayjs from "dayjs";
import "./builds.css";

const Builds = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const user = useSelector((s) => s.user);
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [builds, setBuilds] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [loadingBuilds, setLoadingBuilds] = useState(false);
  const [editingBuild, setEditingBuild] = useState(null);
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Загрузка списка программ
  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = () => {
    setLoadingPrograms(true);
    const progCompany = process.env.REACT_APP_PROG_COMPANY;
    const url = progCompany ? `dict/prog?progCompany=${encodeURIComponent(progCompany)}` : "dict/prog";

    serverFetch(url)
      .then((data) => {
        setLoadingPrograms(false);
        setPrograms(data);
        
        if (data.length > 0) {
          // Пытаемся восстановить выбор из localStorage
          const savedProgId = localStorage.getItem('builds_selectedProgram');
          const savedProgramExists = savedProgId && data.some(p => p.id === parseInt(savedProgId));
          
          if (savedProgramExists) {
            setSelectedProgram(parseInt(savedProgId));
          } else {
            setSelectedProgram(data[0].id);
          }
        }
      })
      .catch((e) => {
        setLoadingPrograms(false);
        message.error(e.userMessage || "Ошибка загрузки списка программ");
      });
  };

  // Сохранение выбранной программы в localStorage
  useEffect(() => {
    if (selectedProgram) {
      localStorage.setItem('builds_selectedProgram', selectedProgram.toString());
    }
  }, [selectedProgram]);

  // Сохранение выбранного релиза в localStorage
  useEffect(() => {
    if (selectedRelease) {
      localStorage.setItem('builds_selectedRelease', selectedRelease);
    }
  }, [selectedRelease]);

  // Загрузка релизов при выборе программы
  useEffect(() => {
    if (selectedProgram) {
      loadReleases(selectedProgram);
      setSelectedRelease(null);
      setBuilds([]);
    }
  }, [selectedProgram]);

  const loadReleases = (progId, keepSelected = false) => {
    setLoadingReleases(true);
    serverFetch("ProgBuild/Releases", {queryParams: { progId } })
      .then((data) => {
        setLoadingReleases(false);
        setReleases(data);
        
        if (data.length > 0 && !keepSelected) {
          // Восстанавливаем выбор релиза из localStorage только при первой загрузке
          if (isInitialLoad) {
            const savedRelease = localStorage.getItem('builds_selectedRelease');
            const savedReleaseExists = savedRelease && data.includes(savedRelease);
            
            if (savedReleaseExists) {
              setSelectedRelease(savedRelease);
            } else {
              setSelectedRelease(data[0]);
            }
            setIsInitialLoad(false);
          } else {
            // При смене программы выбираем первый релиз
            setSelectedRelease(data[0]);
          }
        }
      })
      .catch((e) => {
        setLoadingReleases(false);
        message.error(e.userMessage || "Ошибка загрузки списка релизов");
      });
  };

  // Загрузка билдов при выборе релиза
  useEffect(() => {
    if (selectedRelease && selectedProgram) {
      loadBuilds(selectedProgram, selectedRelease);
    }
  }, [selectedRelease, selectedProgram]);

  const loadBuilds = (progId, release) => {
    setLoadingBuilds(true);
    serverFetch(`ProgBuild/${progId}`, {
      queryParams: { release }
    })
      .then((data) => {
        setLoadingBuilds(false);
        setBuilds(data);
      })
      .catch((e) => {
        setLoadingBuilds(false);
        message.error(e.userMessage || "Ошибка загрузки списка билдов");
      });
  };

  const handleProgramChange = (value) => {
    setSelectedProgram(value);
  };

  const handleReleaseClick = (release) => {
    setSelectedRelease(release);
  };

  const handleDownload = (url) => {
    window.open(url, '_blank');
  };

  const handleAddBuild = () => {
    setEditingBuild(null);
    setShowBuildModal(true);
  };

  const handleEditBuild = (build) => {
    setEditingBuild(build);
    setShowBuildModal(true);
  };

  const handleCloseBuildModal = (refresh) => {
    setShowBuildModal(false);
    setEditingBuild(null);
    if (refresh && selectedProgram && selectedRelease) {
      loadBuilds(selectedProgram, selectedRelease);
      loadReleases(selectedProgram, true); // Обновляем список релизов без изменения выбора
    }
  };

  const buildsListContent =
    loadingBuilds ? (
      <div style={{ textAlign: "center", padding: "40px" }}>
        <Spin size="large" />
      </div>
    ) : builds.length > 0 ? (
      builds.map((build) => (
        <div key={build.version} className={`builds-build-card${isMobile ? " builds-build-card--mobile" : ""}`}>
          <div className="builds-build-header">
            <div className="builds-build-version-container">
              <span className="builds-build-version">{build.version}</span>
              {build.createdOn && (
                <span className="builds-build-date">{dayjs(build.createdOn).format("DD.MM.YYYY")}</span>
              )}
            </div>
            <Space>
              {build.url &&
                (isMobile ? (
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(build.url)}
                    aria-label="Скачать"
                    className="builds-build-icon-btn"
                  />
                ) : (
                  <Button type="primary" onClick={() => handleDownload(build.url)}>
                    Скачать
                  </Button>
                ))}
              {user?.admin &&
                (isMobile ? (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleEditBuild(build)}
                    aria-label="Изменить"
                    className="builds-build-icon-btn"
                  />
                ) : (
                  <Button onClick={() => handleEditBuild(build)}>Изменить</Button>
                ))}
            </Space>
          </div>
          <div
            className="builds-build-description"
            dangerouslySetInnerHTML={{ __html: build.description || "Описание дистрибутива" }}
          />
        </div>
      ))
    ) : selectedRelease ? (
      <div className="builds-empty-state">
        <p>Нет доступных билдов для релиза {selectedRelease}</p>
      </div>
    ) : (
      <div className="builds-empty-state">
        <p>Выберите релиз для просмотра билдов</p>
      </div>
    );

  return (
    <div className={`builds-container${isMobile ? " builds-container--mobile" : ""}`}>
      {isMobile ? (
        <>
          <div className="builds-mobile-toolbar">
            <Select
              className="builds-program-select"
              placeholder="Выберите программу"
              value={selectedProgram}
              onChange={handleProgramChange}
              loading={loadingPrograms}
              options={programs.map((prog) => ({ label: prog.name, value: prog.id }))}
            />
            <Select
              className="builds-release-select"
              placeholder={releases.length === 0 && !loadingReleases ? "Нет релизов" : "Выберите релиз"}
              value={selectedRelease}
              onChange={handleReleaseClick}
              loading={loadingReleases}
              disabled={!selectedProgram || releases.length === 0}
              options={releases.map((r) => ({ label: r, value: r }))}
            />
            {user?.admin ? (
              <Button type="primary" onClick={handleAddBuild} className="builds-add-button" block disabled={!selectedProgram}>
                Добавить
              </Button>
            ) : null}
          </div>
          <div className="builds-mobile-list">{buildsListContent}</div>
        </>
      ) : (
        <>
          <div className="builds-left-panel">
            <Select
              className="builds-program-select"
              placeholder="Выберите программу"
              value={selectedProgram}
              onChange={handleProgramChange}
              loading={loadingPrograms}
              options={programs.map((prog) => ({ label: prog.name, value: prog.id }))}
            />

            <div className="builds-releases-container">
              {loadingReleases ? (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <Spin />
                </div>
              ) : releases.length > 0 ? (
                releases.map((release) => (
                  <div
                    key={release}
                    className={`builds-release-item ${selectedRelease === release ? "selected" : ""}`}
                    onClick={() => handleReleaseClick(release)}
                  >
                    <div className="builds-release-bullet" />
                    <span>{release}</span>
                  </div>
                ))
              ) : (
                <div className="builds-empty-state">
                  <p>Нет доступных релизов</p>
                </div>
              )}
            </div>
            {user?.admin ? (
              <Button type="primary" onClick={handleAddBuild} className="builds-add-button" block disabled={!selectedProgram}>
                Добавить
              </Button>
            ) : null}
          </div>

          <div className="builds-right-panel">{buildsListContent}</div>
        </>
      )}
      {showBuildModal && (
        <Build
          progId={selectedProgram}
          progName={programs.find((p) => p.id === selectedProgram)?.name || ""}
          build={editingBuild}
          onClose={handleCloseBuildModal}
        />
      )}
    </div>
  );
};

export default Builds;